import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import {
	makeBrain,
	makeGlobe,
	makeIdeaBulb,
	makeNeuralNet,
	makeRobot,
	makeRocket,
} from "../v2/particleShapes";

const SHAPES = {
	bulb: makeIdeaBulb,
	rocket: makeRocket,
	globe: makeGlobe,
	brain: makeBrain,
	neural: makeNeuralNet,
	robot: makeRobot,
};

function hexToRgb(hex) {
	const n = parseInt(hex.slice(1), 16);
	return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/**
 * Lightweight 2D point-cloud renderer for the division stage: the same
 * particle language as the hero sculptures, scaled for the disc slot.
 * Slow yaw spin + twinkle, additive blending, static frame under
 * prefers-reduced-motion, paused offscreen.
 */
/**
 * backCull > 0 fades out points as they rotate behind the sculpture's
 * mid-plane (higher = faster fade). Use it for shapes whose far side
 * reads as pixel noise inside the silhouette (e.g. the brain) instead
 * of the default see-through cloud.
 */
export default function ParticleSculpture({
	shape,
	accent,
	count,
	pointScale = 1,
	backCull = 0,
}) {
	const canvasRef = useRef(null);

	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return undefined;
		const ctx = canvas.getContext("2d");

		const reducedMotion = window.matchMedia(
			"(prefers-reduced-motion: reduce)",
		).matches;
		const isMobile = window.innerWidth < 992;
		const dpr = Math.min(window.devicePixelRatio || 1, 2);
		const [r, g, b] = hexToRgb(accent);

		const COUNT = count || (isMobile ? 1700 : 1600);
		const pts = (SHAPES[shape] || makeIdeaBulb)(COUNT);
		// tile mode (small pointScale) wants an even, fine grain: tight size
		// variance and almost no bright outliers, or stray fat dots break
		// the read at small scale
		const tight = pointScale < 0.8;
		const seeds = new Float32Array(COUNT * 3);
		for (let i = 0; i < COUNT; i++) {
			seeds[i * 3] = tight
				? 0.82 + Math.random() * 0.34
				: 0.6 + Math.random() * 0.9; // size
			seeds[i * 3 + 1] = Math.random() * Math.PI * 2; // twinkle phase
			seeds[i * 3 + 2] = Math.random() < (tight ? 0.04 : 0.09) ? 1 : 0; // white sparkle
		}

		let width = 0;
		let height = 0;
		let rafId = null;
		let running = false;
		let inView = true;
		let yaw = Math.random() * Math.PI * 2;
		let time = 0;

		function resize() {
			// offsetWidth/Height: layout size, immune to the disc's scale-in
			// transform (getBoundingClientRect during the entrance animation
			// under-sizes the backing store by ~18%)
			width = canvas.parentElement.offsetWidth;
			height = canvas.parentElement.offsetHeight;
			canvas.width = Math.round(width * dpr);
			canvas.height = Math.round(height * dpr);
			ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
		}
		resize();

		function draw() {
			time += 1 / 60;
			yaw += 0.005;
			ctx.clearRect(0, 0, width, height);
			ctx.globalCompositeOperation = "lighter";

			const cx = width / 2;
			const cy = height / 2;
			const R = Math.min(width, height) * 0.34;
			const tilt = 0.16;
			const cosY = Math.cos(yaw);
			const sinY = Math.sin(yaw);
			const cosT = Math.cos(tilt);
			const sinT = Math.sin(tilt);
			const focal = 4.2;

			for (let i = 0; i < COUNT; i++) {
				const x = pts[i * 3];
				const y = pts[i * 3 + 1];
				const z = pts[i * 3 + 2];
				const x1 = x * cosY + z * sinY;
				const z1 = -x * sinY + z * cosY;
				const y1 = y * cosT - z1 * sinT;
				const z2 = y * sinT + z1 * cosT;
				const s = focal / (focal + z2);
				const px = cx + x1 * R * s;
				const py = cy + y1 * R * s;
				const twinkle =
					0.72 + 0.28 * Math.sin(time * (1 + seeds[i * 3]) * 2 + seeds[i * 3 + 1]);
				// tiny points read dimmer, so compensate alpha when scaled down
				let alpha = Math.max(
					0,
					(0.55 - z2 * 0.42) * twinkle * (pointScale < 0.8 ? 1.35 : 1),
				);
				if (backCull && z2 > 0) alpha *= Math.max(0, 1 - z2 * backCull);
				if (alpha <= 0.03) continue;
				// the 0.3px floor defeats very small pointScale values, so let
				// fine-grain tiles ("pixel" look) drop below it
				const size = Math.max(
					pointScale < 0.4 ? 0.18 : 0.3,
					(1.1 * s - z2 * 0.32) * seeds[i * 3] * pointScale,
				);
				ctx.fillStyle = seeds[i * 3 + 2]
					? `rgba(255,255,255,${(alpha * 0.7).toFixed(3)})`
					: `rgba(${Math.min(255, r + 28)},${Math.min(255, g + 28)},${Math.min(255, b + 28)},${alpha.toFixed(3)})`;
				ctx.beginPath();
				ctx.arc(px, py, size, 0, Math.PI * 2);
				ctx.fill();
			}
			ctx.globalCompositeOperation = "source-over";
		}

		function loop() {
			rafId = requestAnimationFrame(loop);
			if (inView) draw();
		}

		function start() {
			if (running || reducedMotion) return;
			running = true;
			rafId = requestAnimationFrame(loop);
		}

		function stop() {
			running = false;
			if (rafId) cancelAnimationFrame(rafId);
			rafId = null;
		}

		const io =
			typeof IntersectionObserver !== "undefined"
				? new IntersectionObserver(([entry]) => {
						inView = entry.isIntersecting;
					})
				: null;
		if (io) io.observe(canvas);

		function onVisibility() {
			if (document.hidden) stop();
			else start();
		}

		let lastW = 0;
		const ro =
			typeof ResizeObserver !== "undefined"
				? new ResizeObserver(() => {
						const w = canvas.parentElement.offsetWidth;
						if (Math.abs(w - lastW) > 1) {
							lastW = w;
							resize();
							if (reducedMotion) draw();
						}
					})
				: null;
		if (ro) ro.observe(canvas.parentElement);

		document.addEventListener("visibilitychange", onVisibility);

		if (reducedMotion) {
			draw();
		} else {
			start();
		}

		return () => {
			stop();
			if (io) io.disconnect();
			if (ro) ro.disconnect();
			document.removeEventListener("visibilitychange", onVisibility);
		};
	}, [shape, accent, count, pointScale, backCull]);

	return <Canvas ref={canvasRef} aria-hidden="true" />;
}

const Canvas = styled.canvas`
  position: absolute;
  inset: -18%;
  width: 136%;
  height: 136%;
  display: block;
  pointer-events: none;
`;
